import * as React from "react";
import {Button, Grid} from "react-bootstrap";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ActionBar} from "../common/ui/actionbar/ActionBar";
import {LcdIcon} from "../common/ui/icon/LcdIcon";
import {MasterDetailLayout} from "../common/ui/layouts/MasterDetailLayout";
import {FileUploadAndInjectedProps, WithFileUpload} from "../common/ui/upload/WithFileUpload";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AddStyleButton} from "./AddStyleButton";
import {DeleteStyleButton} from "./DeleteStyleButton";
import {StylesFilter} from "./filter/StylesFilter";
import {Style} from "./model";
import {StylesList} from "./StylesList";
import {StylesListDetailSection} from "./StylesListDetailSection";
import {StylesOutdatedWarning} from "./StylesOutdatedWarning";
import {IchStyleEditorPopup} from "./IchStyleEditorPopup";
import {StyleEditorPopup} from "../styleEditor/StyleEditorPopup"
import {SortDirection, SortSettings, updateSortIndicators} from "../common/ui/table/model";
import {COLUMN_SORT_SETTINGS_STYLES, localRepository} from "../common/LocalRepository";


interface StylesListState {
    selection: Style[];
    isIchStyleEditorOpen: boolean;
    isVectorStyleEditorOpen: boolean;
  sortSettings?: SortSettings | null
}

class StylesPageReactComponent extends React.Component<InjectedIntlProps & FileUploadAndInjectedProps & WithApiProperties, StylesListState> {
    constructor(props) {
        super(props);
        this.state = {
            selection: [],
            isIchStyleEditorOpen: false,
            isVectorStyleEditorOpen: false,
          sortSettings: null,
        };
    }

  componentDidMount() {
    let sortSettings: SortSettings = localRepository.getOptions<SortSettings>(COLUMN_SORT_SETTINGS_STYLES);
    if (!sortSettings) {
      sortSettings = {
        column: "title",
        direction: SortDirection.asc
      }
      localRepository.setOptions(COLUMN_SORT_SETTINGS_STYLES, sortSettings);
    }
    this.setState({sortSettings});
  }

    handleRowSelect = (selectedStyles: Style[]) => {
        this.setState({selection: selectedStyles});
    }

    clearSelection = () => this.handleRowSelect([]);


    render() {
        const {selection, isIchStyleEditorOpen, isVectorStyleEditorOpen} = this.state;
        const noSelection = selection.length === 0;

        const MasterPane = (
            <Grid fluid={true}>
                <ActionBar>
                    <div>
                        <Button id={"styleFileUploadButton"}
                                bsStyle="primary"
                                className="style-data-tour-upload-button"
                                onClick={this.props.openUpload}>
                            <LcdIcon icon="upload"/>
                            <FormattedMessage id="studio.styles-page.upload-button"
                                              defaultMessage="Upload SLD"/>
                        </Button>
                    </div>
                    <AddStyleButton onAddSuccess={this.clearSelection}/>
                    <div>
                        <Button id={"styleIchButton"}
                                bsStyle="primary"
                                className="style-data-tour-upload-button"
                                onClick={this.openIchStyleEditor}>
                            <LcdIcon icon="plus"/>
                            <FormattedMessage id="studio.styles-page.ich-add-button"
                                              defaultMessage="Add Image Chain Style"/>
                        </Button>
                    </div>
                    <div>
                        <Button id={"styleVectorButton"}
                                bsStyle="primary"
                                className="style-data-tour-upload-button"
                                onClick={this.openVectorStyleEditor}>
                            <LcdIcon icon="plus"/>
                            <FormattedMessage id="studio.styles-page.vector-add-button"
                                              defaultMessage="Add Vector Style"/>
                        </Button>
                    </div>


                </ActionBar>

                <StylesFilter notifyFilterChanged={this.clearSelection}/>

                <ActionBar>
                    <DeleteStyleButton disabled={noSelection} styles={selection} onDeleteSuccess={this.clearSelection}
                                       onDeleteError={Function.prototype}
                                       onDeleteWarnings={this.clearSelection} onCloseWarnings={Function.prototype}/>
                </ActionBar>

                <div> {/* This empty div is necessary! it's used by ControlRoomTable to calculate its dimensions.*/}
                    {isIchStyleEditorOpen && <IchStyleEditorPopup onClose={this.closeIchStyleEditor}/>}
                    {isVectorStyleEditorOpen && <StyleEditorPopup onClose={this.closeVectorStyleEditor}/>}
                    <StylesList className="style-data-tour-styles-list"
                                onRowSelect={this.handleRowSelect}
                                clearSelection={noSelection}
                                sortSettings={this.state?.sortSettings}
                                onSortChange={this.onSortChange}
                    />
                </div>
                <StylesOutdatedWarning numberOfRemovedDataItems={0} numberOfNewDataItems={0} numberOfNewFolders={0}
                                       numberOfRemovedFolders={0}/>
            </Grid>
        );

        const detailPane = <StylesListDetailSection selection={selection}/>;

        return (
            <MasterDetailLayout masterPane={MasterPane} detailPane={detailPane}/>
        );
    }

    private onSortChange = (columnKey: string) => {
        this.clearSelection();
        this.setState((prevState) => {
          if (prevState.sortSettings.column === columnKey) {
            const nextDir = prevState.sortSettings.direction === SortDirection.asc ? SortDirection.desc
                                                                                   : SortDirection.asc;
            const sortSettings: SortSettings = {column: columnKey, direction: nextDir};
            localRepository.setOptions(COLUMN_SORT_SETTINGS_STYLES, sortSettings);
            updateSortIndicators(sortSettings.column, sortSettings.direction, columnKey);
            return {sortSettings}
            }
          localRepository.setOptions(COLUMN_SORT_SETTINGS_STYLES, {column: columnKey, direction: SortDirection.asc});
          updateSortIndicators(columnKey, SortDirection.asc, columnKey);
          return {sortSettings: {column: columnKey, direction: SortDirection.asc}};
        });
    }

    private closeIchStyleEditor = () => {
        this.setState({isIchStyleEditorOpen: false});
    }

    private openIchStyleEditor = () => {
        this.setState({isIchStyleEditorOpen: true});
    }

    private openVectorStyleEditor = () => {
        this.setState({isVectorStyleEditorOpen: true});
    }

    private closeVectorStyleEditor = () => {
        this.setState({isVectorStyleEditorOpen: false});
    }
}

export const StylesPageComponent = WithFileUpload()(StylesPageReactComponent);
export const StylesPage = WithApi(injectIntl(StylesPageComponent));